import { type FunctionComponent } from 'react';
import classNames from 'classnames';
import { ForgeIcon, ForgeIconButton, ForgePopover } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import './index.scss';

interface Props {
  label: string;
  popoverContent: string | JSX.Element;
  popoverIconId: string; // needs to be unique
  // for accessibility, please provide at least one of these two aria props for the popover
  ariaLabelledById?: string; // this is the id for the popover title
  ariaLabel?: string;
  className?: string;
  popoverMaxWidth?: number;
  /**
   * (Default: undefined)
   * String that is the id of the element that this label describes. Highly
   * recommended for max accessibility.
   */
  htmlFor?: string;
}

const LabelWithHelperText: FunctionComponent<Props> = ({
  label,
  popoverContent,
  ariaLabelledById,
  ariaLabel,
  popoverIconId: id,
  className,
  htmlFor,
  popoverMaxWidth = 300,
}: Props) => {
  const tooltipId = `${id}-tooltip`;
  return (
    <div>
      <div className={classNames('label-with-helper', className)}>
        <label {...(htmlFor ? { htmlFor: htmlFor } : {})}>{label}</label>
        <div className="icon-wrapper">
          <ForgeIconButton
            id={id}
            type="button"
            density="small"
            aria-label={I18n.t('shared.components.label_with_helper_text.icon_button_aria_label', {
              name: ariaLabel || label,
            })}
            aria-describedby={tooltipId}
            className="label-icon-button"
          >
            <ForgeIcon name="info_outline" className="label-icon" aria-hidden="true" />
          </ForgeIconButton>
          <ForgePopover
            anchor={id}
            role="tooltip"
            triggerType={['hover', 'focus']}
            hoverDismissDelay={100}
            {...(ariaLabelledById ? { 'aria-labelledby': ariaLabelledById } : {})}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          >
            <div
              id={tooltipId}
              style={{ width: `${popoverMaxWidth}px`, padding: '8px' }}
              className="forge-typography--body1 body-section"
            >
              {popoverContent}
            </div>
          </ForgePopover>
        </div>
      </div>
    </div>
  );
};

export default LabelWithHelperText;
